import {
  css,
  type SerializedStyles,
} from '@emotion/react';
import { forwardRef } from 'react';
import { useTheme } from '../../../themes/theme.hooks';
import { type ThemeProps } from '../../../types/themeProps';
import { CSS_CLASSNAME as ROUND_BUTTON_CLASSNAME } from './constants';
import {
  getHoverCss as getRoundButtonHoverCss,
  getRoundButtonFontSize,
  RoundButtonComponent,
  type RoundButtonProps,
  RoundButtonSize,
} from './roundButton.component';
import { RoundButtonStyle } from './roundButton.styles';
import { getTextColorForButtonStyle } from './roundButtonWithText.helpers';

const TEXT_ITEM_OF_ROUND_BUTTON_CLASSNAME = 'text-item-of_round_button';

const getWrapperStyle = ({ isDisabled, theme, btnStyle, btnSize, textColor, textHoverColor }: ThemeProps<{
  btnStyle: RoundButtonStyle; btnSize: RoundButtonSize; isDisabled?: boolean; textColor?: string; textHoverColor?: string;
}>) => {
  const hoverStyles = getRoundButtonHoverCss({ buttonStyle: btnStyle, theme });
  const textColors = getTextColorForButtonStyle(btnStyle, theme);

  return css({
    alignItems: 'center',
    color: textColor || textColors.color,
    cursor: 'default',
    display: 'flex',
    fontSize: getRoundButtonFontSize(btnSize),
    gap: 8,

    ...(isDisabled ? {} : { '&:hover': {
      [`.${ROUND_BUTTON_CLASSNAME}`]: hoverStyles,
      [`.${TEXT_ITEM_OF_ROUND_BUTTON_CLASSNAME}`]: {
        color: textHoverColor || textColors.colorHover,
      },
    } }),
  });
};

export type RoundButtonWrapperProps = RoundButtonProps & Readonly<{
  isTextHidden?: boolean;
  text: string;
  textColor?: string;
  textHoverColor?: string;
  wrapperStyle?: SerializedStyles;

  onClick?: () => void;
}>;

export const RoundButtonWithTextComponent = forwardRef<HTMLSpanElement, RoundButtonWrapperProps>(({
  buttonStyle,
  className,
  icon,
  isDisabled,
  isTextHidden,
  onClick,
  size,
  text,
  textColor,
  textHoverColor,
  tooltipLabel,
  tooltipPlacement,
  tooltipBehavior,
  wrapperStyle,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const guaranteedBtnStyle = isDisabled ? RoundButtonStyle.Disabled : buttonStyle || RoundButtonStyle.Primary;
  const guaranteedSize = size || RoundButtonSize.Large;

  return (
    <span
      {...restProps}
      ref={ref}
      css={[getWrapperStyle({
        theme,
        btnStyle: guaranteedBtnStyle,
        btnSize: guaranteedSize,
        isDisabled, textColor,
        textHoverColor,
      }), wrapperStyle]}
      onClick={isDisabled ? undefined : onClick}
    >
      <RoundButtonComponent
        buttonStyle={guaranteedBtnStyle}
        className={className}
        icon={icon}
        isDisabled={isDisabled}
        onClick={undefined}
        size={guaranteedSize}
        tooltipLabel={tooltipLabel}
        tooltipPlacement={tooltipPlacement}
        tooltipBehavior={tooltipBehavior}
      />
      {!isTextHidden && (
        <div
          className={TEXT_ITEM_OF_ROUND_BUTTON_CLASSNAME}
        >
          {text}
        </div>
      )}
    </span>
  );
});
